'use client'

import ImageWithFallback from '@/components/common/image-with-fallback'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import useSWR from 'swr'

export const STANDINGS_REFRESH_INTERVAL_MS = 60_000
const ORDINAL_SUFFIXES = ['th', 'st', 'nd', 'rd']

function getNumberWithOrdinal(number) {
  const remainder = number % 100
  const suffix =
    ORDINAL_SUFFIXES[(remainder - 20) % 10] ||
    ORDINAL_SUFFIXES[remainder] ||
    ORDINAL_SUFFIXES[0]

  return `${number}${suffix}`
}

async function fetchTimeAttackStandings([, eventId]) {
  const response = await fetch(`/api/v4/events/${eventId}/iracing-standings`)
  const body = await response.json()

  if (!response.ok) {
    throw new Error(body.error || 'Failed to fetch standings from iRacing')
  }

  return Array.isArray(body.data) ? body.data : []
}

function TimeAttackCutoffRow({ cutoff }) {
  return (
    <tr data-time-attack-cutoff="">
      <TableCell className="h-10 px-2 py-0" colSpan={4}>
        <span className="sr-only">
          Qualification cut-off after position {cutoff.position}
        </span>
        <div
          aria-hidden="true"
          className="relative flex items-center justify-center"
        >
          <div className="absolute inset-x-0 h-0.5 bg-error-500" />
          <span className="semibold-caps relative rounded-full bg-error-500 px-3 py-1 text-black">
            {cutoff.label}
          </span>
        </div>
      </TableCell>
    </tr>
  )
}

export function TimeAttackResultsTable({ results, cutoff = null }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[100px] px-2">
            <p className="text-left">POS</p>
          </TableHead>
          <TableHead className="px-2">NAME</TableHead>
          <TableHead className="w-[120px] px-2">
            <p>LAPS</p>
          </TableHead>
          <TableHead className="w-[150px] px-2">
            <p>BEST LAP</p>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {results.map((result) => (
          <RowWithOptionalCutoff
            cutoff={cutoff}
            key={result.custId}
            result={result}
          />
        ))}
      </TableBody>
    </Table>
  )
}

function RowWithOptionalCutoff({ result, cutoff }) {
  return (
    <>
      <TableRow className="group transition-colors data-[state=selected]:bg-slate-100 dark:hover:bg-neutral-blue-800 dark:data-[state=selected]:bg-slate-800">
        <TableCell className="px-2">
          <div
            className={cn(
              'flex aspect-square h-16 w-full items-center justify-center rounded-sm bg-neutral-blue-800 p-4 text-p-2 font-semibold text-shades-0 group-first:bg-neutral-blue-700',
              {
                'text-medals-gold': result.position === 1,
                'text-medals-silver': result.position === 2,
                'text-medals-bronze': result.position === 3,
              },
            )}
          >
            {getNumberWithOrdinal(result.position)}
          </div>
        </TableCell>
        <TableCell className="px-2">
          <div className="flex items-center gap-3">
            <div className="relative size-12 overflow-hidden rounded-full">
              <ImageWithFallback
                width={48}
                height={48}
                src={result.user.avatarUrl}
                alt={`${result.user.name} profile picture`}
              />
            </div>
            <div className="flex flex-col">
              <p className="text-p-2">{result.user.name}</p>
              <p className="text-cap text-neutral-blue-500">
                {result.displayName}
              </p>
            </div>
          </div>
        </TableCell>
        <TableCell className="px-2">
          <p>{result.lapsCompleted}</p>
        </TableCell>
        <TableCell className="px-2">
          <p className="flex w-min rounded-sm ordinal tabular-nums group-first:bg-[#5811CB] group-first:px-4 group-first:py-3">
            {result.bestLapTimeFormatted}
          </p>
        </TableCell>
      </TableRow>
      {cutoff && result.position === cutoff.position ? (
        <TimeAttackCutoffRow cutoff={cutoff} />
      ) : null}
    </>
  )
}

export default function IRacingTimeAttackStandings({ eventId, cutoff = null }) {
  const {
    data: iRacingStandings,
    error: iRacingStandingsError,
    isLoading: iRacingStandingsLoading,
  } = useSWR(
    eventId ? ['iracing-standings', eventId] : null,
    fetchTimeAttackStandings,
    {
      refreshInterval: STANDINGS_REFRESH_INTERVAL_MS,
      refreshWhenHidden: false,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    },
  )

  if (!eventId) return <p>Leaderboard will be live soon!</p>

  if (iRacingStandingsLoading) {
    return (
      <Skeleton
        aria-label="Loading time attack standings"
        className="h-[300px] w-full"
        role="status"
      />
    )
  }

  if (iRacingStandingsError) {
    return <p role="alert">Error fetching standings from iRacing...</p>
  }

  if (!iRacingStandings || iRacingStandings.length === 0) {
    return <p>No laps set yet!</p>
  }

  return <TimeAttackResultsTable cutoff={cutoff} results={iRacingStandings} />
}
